/**
 * Design tokens exposed to JavaScript / TypeScript.
 *
 * These are read at runtime from the CSS custom properties defined in
 * `styles/tokens.css`, so JS-driven styles automatically respect the active
 * theme (light / dark) without duplicating the palette in TS.
 *
 * Usage:
 *   import { tokens, css } from '../design/tokens';
 *   <div style={{ background: tokens.color.primary, padding: tokens.space[4] }} />
 *
 *   // Or in style strings (preferred so dark-mode flips automatically):
 *   <div style={{ background: css.color.primary }} />     // -> "var(--pm-color-primary)"
 */

const cssVar = (name: string): string => `var(--${name})`;

/** Compile-time strings that point at the live CSS variable values. */
export const css = {
  color: {
    primary:        cssVar('pm-color-primary'),
    primaryStrong:  cssVar('pm-color-primary-strong'),
    primarySoft:    cssVar('pm-color-primary-soft'),
    primary50:      cssVar('pm-color-primary-50'),

    secondary:      cssVar('pm-color-secondary'),
    secondarySoft:  cssVar('pm-color-secondary-soft'),

    accent:         cssVar('pm-color-accent'),
    accentStrong:   cssVar('pm-color-accent-strong'),
    accentSoft:     cssVar('pm-color-accent-soft'),

    success:        cssVar('pm-color-success'),
    successSoft:    cssVar('pm-color-success-soft'),
    warning:        cssVar('pm-color-warning'),
    warningSoft:    cssVar('pm-color-warning-soft'),
    danger:         cssVar('pm-color-danger'),
    dangerSoft:     cssVar('pm-color-danger-soft'),
    info:           cssVar('pm-color-info'),
    infoSoft:       cssVar('pm-color-info-soft'),

    grocery:        cssVar('pm-color-grocery'),
    food:           cssVar('pm-color-food'),
    rides:          cssVar('pm-color-rides'),
    veg:            cssVar('pm-color-veg'),
    nonveg:         cssVar('pm-color-nonveg'),
    halal:          cssVar('pm-color-halal'),

    bg:             cssVar('pm-color-bg'),
    bgElevated:     cssVar('pm-color-bg-elevated'),
    surface:        cssVar('pm-color-surface'),
    surface2:       cssVar('pm-color-surface-2'),
    surface3:       cssVar('pm-color-surface-3'),
    surfaceHover:   cssVar('pm-color-surface-hover'),

    text:           cssVar('pm-color-text'),
    text2:          cssVar('pm-color-text-2'),
    textMuted:      cssVar('pm-color-text-muted'),
    textSubtle:     cssVar('pm-color-text-subtle'),
    textOnPrimary:  cssVar('pm-color-text-on-primary'),
    textOnAccent:   cssVar('pm-color-text-on-accent'),

    border:         cssVar('pm-color-border'),
    borderStrong:   cssVar('pm-color-border-strong'),
    divider:        cssVar('pm-color-divider'),

    rating:         cssVar('pm-color-rating'),
  },
  shadow: {
    xs:           cssVar('pm-shadow-xs'),
    sm:           cssVar('pm-shadow-sm'),
    card:         cssVar('pm-shadow-card'),
    md:           cssVar('pm-shadow-md'),
    cardHover:    cssVar('pm-shadow-card-hover'),
    lg:           cssVar('pm-shadow-lg'),
    brand:        cssVar('pm-shadow-brand'),
    accent:       cssVar('pm-shadow-accent'),
    focus:        cssVar('pm-shadow-focus'),
  },
  radius: {
    xs:     cssVar('pm-radius-xs'),
    sm:     cssVar('pm-radius-sm'),
    md:     cssVar('pm-radius-md'),
    lg:     cssVar('pm-radius-lg'),
    xl:     cssVar('pm-radius-xl'),
    xl2:    cssVar('pm-radius-2xl'),
    card:   cssVar('pm-radius-card'),
    pill:   cssVar('pm-radius-pill'),
  },
  space: {
    0: cssVar('pm-space-0'),
    1: cssVar('pm-space-1'),
    2: cssVar('pm-space-2'),
    3: cssVar('pm-space-3'),
    4: cssVar('pm-space-4'),
    5: cssVar('pm-space-5'),
    6: cssVar('pm-space-6'),
    8: cssVar('pm-space-8'),
    10: cssVar('pm-space-10'),
    12: cssVar('pm-space-12'),
    16: cssVar('pm-space-16'),
  },
  text: {
    xs:    cssVar('pm-text-xs'),
    sm:    cssVar('pm-text-sm'),
    base:  cssVar('pm-text-base'),
    md:    cssVar('pm-text-md'),
    lg:    cssVar('pm-text-lg'),
    xl:    cssVar('pm-text-xl'),
    xl2:   cssVar('pm-text-2xl'),
    xl3:   cssVar('pm-text-3xl'),
    xl4:   cssVar('pm-text-4xl'),
  },
  weight: {
    regular:   cssVar('pm-weight-regular'),
    medium:    cssVar('pm-weight-medium'),
    semibold:  cssVar('pm-weight-semibold'),
    bold:      cssVar('pm-weight-bold'),
    extrabold: cssVar('pm-weight-extrabold'),
  },
  font: {
    display: cssVar('pm-font-display'),
    body:    cssVar('pm-font-body'),
  },
  motion: {
    fast:     cssVar('pm-motion-fast'),
    base:     cssVar('pm-motion-base'),
    slow:     cssVar('pm-motion-slow'),
    standard: cssVar('pm-easing-standard'),
    emphasized: cssVar('pm-easing-emphasized'),
  },
  z: {
    base:        cssVar('pm-z-base'),
    sticky:      cssVar('pm-z-sticky'),
    bottomNav:   cssVar('pm-z-bottom-nav'),
    navbar:      cssVar('pm-z-navbar'),
    mobileCart:  cssVar('pm-z-mobile-cart'),
    sheet:       cssVar('pm-z-sheet'),
    modal:       cssVar('pm-z-modal'),
    toast:       cssVar('pm-z-toast'),
    tooltip:     cssVar('pm-z-tooltip'),
  },
} as const;

/** Legacy alias for code that imports `tokens` instead of `css`. */
export const tokens = css;

export type Tokens = typeof css;

